<template>
  <div class="basic-project-data-tab">
    <div class="tab-header">
      <h2>Basic Project Data</h2>
      <p class="tab-description">
        Fundamental project information and profile
      </p>
    </div>

    <form @submit.prevent="validateAndSubmit" class="project-form">
      
      <!-- Basic Information -->
      <section class="form-section">
        <div class="section-header">
          <h3>Basic Information</h3>
          <span class="required-indicator">* Required fields</span>
        </div>
        
        <div class="form-grid">
          <div class="form-group full-width">
            <label for="projectName">Project Name *</label>
            <input 
              id="projectName"
              v-model="formData.projectName"
              type="text" 
              required
              placeholder="Enter the official project name"
              :disabled="viewMode"
              @input="updateData"
            />
            <div v-if="errors.projectName" class="error-message">{{ errors.projectName }}</div>
          </div>

          <div class="form-group full-width">
            <label for="implementingAgency">Implementing Government Agency *</label>
            <input 
              id="implementingAgency"
              v-model="formData.implementingAgency"
              type="text" 
              required
              placeholder="Name of the responsible government agency"
              :disabled="viewMode"
              @input="updateData"
            />
            <div v-if="errors.implementingAgency" class="error-message">{{ errors.implementingAgency }}</div>
          </div>

          <div class="form-group">
            <label for="stage">Stage *</label>
            <select 
              id="stage"
              v-model="formData.stage"
              required
              :disabled="viewMode"
              @change="updateData"
            >
              <option value="">Select project stage</option>
              <option value="concept-note">Concept Note</option>
              <option value="preliminary-analysis">Preliminary Analysis</option>
              <option value="prefeasibility-study">Prefeasibility Study</option>
              <option value="feasibility-study">Feasibility Study</option>
            </select>
            <div v-if="errors.stage" class="error-message">{{ errors.stage }}</div>
          </div>

          <div class="form-group">
            <label for="submissionDate">Date of Submission *</label>
            <input 
              id="submissionDate"
              v-model="formData.submissionDate"
              type="date" 
              required
              :disabled="viewMode"
              @input="updateData"
            />
            <div v-if="errors.submissionDate" class="error-message">{{ errors.submissionDate }}</div>
          </div>

          <div class="form-group full-width">
            <label class="checkbox-label">
              <span class="checkbox-text">Is this a privately initiated proposal? *</span>
              <div class="radio-group yes-no">
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.privatelyInitiated" 
                    value="yes"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  Yes
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.privatelyInitiated" 
                    value="no"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  No
                </label>
              </div>
            </label>
            <div v-if="errors.privatelyInitiated" class="error-message">{{ errors.privatelyInitiated }}</div>
          </div>
        </div>
      </section>

      <!-- Section A: Project Profile -->
      <section class="form-section">
        <div class="section-header">
          <h3>Section A: Project Profile</h3>
          <span class="section-description">Detailed project characteristics and specifications (Optional)</span>
        </div>
        
        <div class="form-grid">
          <div class="form-group full-width">
            <label for="location">Location</label>
            <input 
              id="location"
              v-model="formData.location"
              type="text" 
              placeholder="Project location (city, region, country)"
              :disabled="viewMode"
              @input="updateData"
            />
            <div v-if="errors.location" class="error-message">{{ errors.location }}</div>
          </div>

          <div class="form-group">
            <label for="sector">Sector</label>
            <select 
              id="sector"
              v-model="formData.sector"
              :disabled="viewMode"
              @change="updateData"
            >
              <option value="">Select sector</option>
              <option value="agriculture">Agriculture</option>
              <option value="education">Education</option>
              <option value="energy">Energy</option>
              <option value="health">Health</option>
              <option value="housing-accommodation">Housing and Accommodation</option>
              <option value="solid-waste">Solid Waste</option>
              <option value="sports-culture-recreation">Sports, Culture and Recreation</option>
              <option value="telecom-ict">Telecom/ICT</option>
              <option value="tourism">Tourism</option>
              <option value="transport">Transport</option>
              <option value="urban-infrastructure">Urban Infrastructure</option>
              <option value="water">Water</option>
              <option value="other">Other</option>
            </select>
            <div v-if="errors.sector" class="error-message">{{ errors.sector }}</div>
          </div>

          <div class="form-group">
            <label for="projectType">Project Type</label>
            <select 
              id="projectType"
              v-model="formData.projectType"
              :disabled="viewMode"
              @change="updateData"
            >
              <option value="">Select project type</option>
              <option value="construction-operations-maintenance">Construction, Operations and Maintenance</option>
              <option value="construction">Construction</option>
              <option value="operations-maintenance">Operations and Maintenance</option>
            </select>
            <div v-if="errors.projectType" class="error-message">{{ errors.projectType }}</div>
          </div>

          <div class="form-group">
            <label for="currency">Currency</label>
            <select 
              id="currency"
              v-model="formData.currency"
              :disabled="viewMode"
              @change="updateData"
            >
              <option value="">Select currency</option>
              <option value="usd-million">USD Million</option>
              <option value="euro-million">Euro Million</option>
              <option value="local-currency-million">Local Currency Million</option>
            </select>
            <div v-if="errors.currency" class="error-message">{{ errors.currency }}</div>
          </div>

          <div class="form-group">
            <label for="projectSize">Project Size</label>
            <div class="input-with-suffix">
              <input 
                id="projectSize"
                v-model.number="formData.projectSize"
                type="number" 
                step="0.1"
                min="0"
                placeholder="0.0"
                :disabled="viewMode"
                @input="updateData"
              />
              <span class="input-suffix">{{ getCurrencyDisplay() }}</span>
            </div>
            <div v-if="errors.projectSize" class="error-message">{{ errors.projectSize }}</div>
          </div>

          <div class="form-group full-width">
            <label for="projectDescription">Project Description</label>
            <textarea 
              id="projectDescription"
              v-model="formData.projectDescription"
              rows="4"
              placeholder="Provide a detailed description of the project, its objectives, and expected outcomes"
              :disabled="viewMode"
              @input="updateData"
            ></textarea>
            <div class="character-count">{{ characterCount }}/1000</div>
          </div>
        </div>
      </section>

      <!-- Section B: Prerequisite -->
      <section class="form-section">
        <div class="section-header">
          <h3>Section B: Prerequisite</h3>
          <span class="section-description">Detailed project characteristics and specifications (Optional)</span>
        </div>
        
        <div class="form-grid">
          <div class="form-group full-width">
            <label class="checkbox-label">
              <span class="checkbox-text">Is the project derived from a national plan or other medium-to long-term strategic document that establishes the development priorities at the highest levels of the government?</span>
              <div class="radio-group">
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.nationalPlanDerived" 
                    value="yes"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  Yes
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.nationalPlanDerived" 
                    value="no"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  No
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.nationalPlanDerived" 
                    value="uncertain"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  Uncertain
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.nationalPlanDerived" 
                    value="skip"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  Skip
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.nationalPlanDerived" 
                    value="na"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  N/A
                </label>
              </div>
            </label>
          </div>

          <div class="form-group full-width">
            <label class="checkbox-label">
              <span class="checkbox-text">Is the project likely to be economically sound and have direct benefits that are significantly greater than the costs?</span>
              <div class="radio-group">
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.economicallySound" 
                    value="yes"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  Yes
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.economicallySound" 
                    value="no"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  No
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.economicallySound" 
                    value="uncertain"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  Uncertain
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.economicallySound" 
                    value="skip"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  Skip
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.economicallySound" 
                    value="na"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  N/A
                </label>
              </div>
            </label>
          </div>

          <div class="form-group full-width">
            <label class="checkbox-label">
              <span class="checkbox-text">Do sector laws, regulations or policies allow private sector participation in the project?</span>
              <div class="radio-group">
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.privateSectorParticipation" 
                    value="yes"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  Yes
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.privateSectorParticipation" 
                    value="no"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  No
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.privateSectorParticipation" 
                    value="uncertain"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  Uncertain
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.privateSectorParticipation" 
                    value="skip"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  Skip
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.privateSectorParticipation" 
                    value="na"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  N/A
                </label>
              </div>
            </label>
          </div>
        </div>
      </section>

      <!-- Section C: Revenue Profile -->
      <section class="form-section">
        <div class="section-header">
          <h3>Section C: Revenue Profile</h3>
          <span class="section-description">Project revenue characteristics and specifications (Optional)</span>
        </div>
        
        <div class="form-grid">
          <div class="form-group full-width">
            <label for="revenueSource">Main source of project revenues</label>
            <select 
              id="revenueSource"
              v-model="formData.revenueSource"
              :disabled="viewMode"
              @change="updateData"
            >
              <option value="">Select revenue source</option>
              <option value="user-charges">User Charges</option>
              <option value="availability-payments">Availability Payments</option>
              <option value="hybrid-payments">Hybrid Payments</option>
            </select>
            <div v-if="errors.revenueSource" class="error-message">{{ errors.revenueSource }}</div>
          </div>
        </div>
      </section>

      <!-- Section D: Government Support -->
      <section class="form-section">
        <div class="section-header">
          <h3>Section D: Government Support</h3>
          <span class="section-description">Government support requirements and commitments (Optional)</span>
        </div>
        
        <div class="form-grid">
          <div class="form-group full-width">
            <label class="checkbox-label">
              <span class="checkbox-text">Is government support required for the project?</span>
              <div class="radio-group yes-no">
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.governmentSupportRequired" 
                    value="yes"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  Yes
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.governmentSupportRequired" 
                    value="no"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  No
                </label>
              </div>
            </label>
            <div v-if="errors.governmentSupportRequired" class="error-message">{{ errors.governmentSupportRequired }}</div>
          </div>

          <div class="form-group">
            <label for="directCommitments">Direct commitments by the government</label>
            <div class="input-with-suffix">
              <input 
                id="directCommitments"
                v-model.number="formData.directCommitments"
                type="number" 
                step="0.1"
                min="0"
                max="100"
                placeholder="0.0"
                :disabled="viewMode"
                @input="updateData"
              />
              <span class="input-suffix">%</span>
            </div>
            <div v-if="errors.directCommitments" class="error-message">{{ errors.directCommitments }}</div>
          </div>

          <div class="form-group">
            <label for="contingentLiabilities">Contingent liabilities</label>
            <div class="input-with-suffix">
              <input 
                id="contingentLiabilities"
                v-model.number="formData.contingentLiabilities"
                type="number" 
                step="0.1"
                min="0"
                max="100"
                placeholder="0.0"
                :disabled="viewMode"
                @input="updateData"
              />
              <span class="input-suffix">%</span>
            </div>
            <div v-if="errors.contingentLiabilities" class="error-message">{{ errors.contingentLiabilities }}</div>
          </div>
        </div>
      </section>

      <!-- Section E: Land Status -->
      <section class="form-section">
        <div class="section-header">
          <h3>Section E: Land Status</h3>
          <span class="section-description">Land requirements and acquisition details (Optional)</span>
        </div>
        
        <div class="form-grid">
          <div class="form-group">
            <label for="landUnit">Land unit</label>
            <select 
              id="landUnit"
              v-model="formData.landUnit"
              :disabled="viewMode"
              @change="updateData"
            >
              <option value="">Select land unit</option>
              <option value="hectares">Hectares</option>
              <option value="acres">Acres</option>
            </select>
            <div v-if="errors.landUnit" class="error-message">{{ errors.landUnit }}</div>
          </div>

          <div class="form-group full-width">
            <label class="checkbox-label">
              <span class="checkbox-text">Is land acquisition required for the project?</span>
              <div class="radio-group">
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.landAcquisitionRequired" 
                    value="yes"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  Yes
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.landAcquisitionRequired" 
                    value="no"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  No
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.landAcquisitionRequired" 
                    value="uncertain"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  Uncertain
                </label>
              </div>
            </label>
            <div v-if="errors.landAcquisitionRequired" class="error-message">{{ errors.landAcquisitionRequired }}</div>
          </div>

          <!-- Conditional fields shown only when 'Yes' is selected -->
          <template v-if="formData.landAcquisitionRequired === 'yes'">
            <div class="form-group">
              <label for="totalLandRequired">Total land required for the project</label>
              <div class="input-with-suffix">
                <input 
                  id="totalLandRequired"
                  v-model.number="formData.totalLandRequired"
                  type="number" 
                  step="0.1"
                  min="0"
                  placeholder="0.0"
                  :disabled="viewMode"
                  @input="updateData"
                />
                <span class="input-suffix">{{ getLandUnitDisplay() }}</span>
              </div>
              <div v-if="errors.totalLandRequired" class="error-message">{{ errors.totalLandRequired }}</div>
            </div>

            <div class="form-group">
              <label for="landAvailable">Land available for the project</label>
              <div class="input-with-suffix">
                <input 
                  id="landAvailable"
                  v-model.number="formData.landAvailable"
                  type="number" 
                  step="0.1"
                  min="0"
                  placeholder="0.0"
                  :disabled="viewMode"
                  @input="updateData"
                />
                <span class="input-suffix">{{ getLandUnitDisplay() }}</span>
              </div>
              <div v-if="errors.landAvailable" class="error-message">{{ errors.landAvailable }}</div>
            </div>

            <div class="form-group full-width">
              <label for="potentialLandOwners">Potential land owners and project affected parties</label>
              <textarea 
                id="potentialLandOwners"
                v-model="formData.potentialLandOwners"
                rows="3"
                placeholder="Describe potential land owners and affected parties"
                :disabled="viewMode"
                @input="updateData"
              ></textarea>
              <div v-if="errors.potentialLandOwners" class="error-message">{{ errors.potentialLandOwners }}</div>
            </div>

            <div class="form-group">
              <label for="landAcquisitionResponsibility">Responsibility of land acquisition</label>
              <select 
                id="landAcquisitionResponsibility"
                v-model="formData.landAcquisitionResponsibility"
                :disabled="viewMode"
                @change="updateData"
              >
                <option value="">Select responsibility</option>
                <option value="public-sector">Public Sector</option>
                <option value="private-sector">Private Sector</option>
                <option value="na">N/A</option>
              </select>
              <div v-if="errors.landAcquisitionResponsibility" class="error-message">{{ errors.landAcquisitionResponsibility }}</div>
            </div>
          </template>
        </div>
      </section>

      <!-- Section F: Value for Money - Quantitative Assessment -->
      <section class="form-section">
        <div class="section-header">
          <h3>Section F: Value for Money - Quantitative Assessment</h3>
          <span class="section-description">Quantitative value assessment metrics (Optional)</span>
        </div>
        
        <div class="form-grid">
          <div class="form-group">
            <label for="valueForMoney">Value for Money from the project</label>
            <input 
              id="valueForMoney"
              v-model.number="formData.valueForMoney"
              type="number" 
              step="0.001"
              min="0"
              placeholder="0.000"
              :disabled="viewMode"
              @input="updateData"
            />
            <div class="field-note">Eg. Enter 0.1 when 10%</div>
            <div v-if="errors.valueForMoney" class="error-message">{{ errors.valueForMoney }}</div>
          </div>

          <div class="form-group">
            <label for="thresholdValueForMoney">Threshold Value for Money</label>
            <input 
              id="thresholdValueForMoney"
              v-model.number="formData.thresholdValueForMoney"
              type="number" 
              step="0.001"
              min="0"
              placeholder="0.000"
              :disabled="viewMode"
              @input="updateData"
            />
            <div class="field-note">Eg. Enter 0.1 when 10%</div>
            <div v-if="errors.thresholdValueForMoney" class="error-message">{{ errors.thresholdValueForMoney }}</div>
          </div>
        </div>
      </section>

      <!-- Section G: Return Expectations -->
      <section class="form-section">
        <div class="section-header">
          <h3>Section G: Return Expectations</h3>
          <span class="section-description">Project return and performance metrics (Optional)</span>
        </div>
        
        <div class="form-grid">
          <div class="form-group">
            <label for="projectIrrBase">Project IRR (Base case)</label>
            <input 
              id="projectIrrBase"
              v-model.number="formData.projectIrrBase"
              type="number" 
              step="0.001"
              placeholder="0.000"
              :disabled="viewMode"
              @input="updateData"
            />
            <div class="field-note">Eg. Enter 0.1 when 10%</div>
            <div v-if="errors.projectIrrBase" class="error-message">{{ errors.projectIrrBase }}</div>
          </div>

          <div class="form-group">
            <label for="projectIrrStress">Project IRR (Stress case)</label>
            <input 
              id="projectIrrStress"
              v-model.number="formData.projectIrrStress"
              type="number" 
              step="0.001"
              placeholder="0.000"
              :disabled="viewMode"
              @input="updateData"
            />
            <div class="field-note">Eg. Enter 0.1 when 10%</div>
            <div v-if="errors.projectIrrStress" class="error-message">{{ errors.projectIrrStress }}</div>
          </div>

          <div class="form-group">
            <label for="thresholdProjectIrr">Threshold Project IRR</label>
            <input 
              id="thresholdProjectIrr"
              v-model.number="formData.thresholdProjectIrr"
              type="number" 
              step="0.001"
              placeholder="0.000"
              :disabled="viewMode"
              @input="updateData"
            />
            <div class="field-note">Eg. Enter 0.1 when 10%</div>
            <div v-if="errors.thresholdProjectIrr" class="error-message">{{ errors.thresholdProjectIrr }}</div>
          </div>

          <div class="form-group">
            <label for="minimumDscrBase">Minimum DSCR (Base case)</label>
            <input 
              id="minimumDscrBase"
              v-model.number="formData.minimumDscrBase"
              type="number" 
              step="0.01"
              min="0"
              placeholder="0.00"
              :disabled="viewMode"
              @input="updateData"
            />
            <div v-if="errors.minimumDscrBase" class="error-message">{{ errors.minimumDscrBase }}</div>
          </div>

          <div class="form-group">
            <label for="minimumDscrStress">Minimum DSCR (Stress case)</label>
            <input 
              id="minimumDscrStress"
              v-model.number="formData.minimumDscrStress"
              type="number" 
              step="0.01"
              min="0"
              placeholder="0.00"
              :disabled="viewMode"
              @input="updateData"
            />
            <div v-if="errors.minimumDscrStress" class="error-message">{{ errors.minimumDscrStress }}</div>
          </div>

          <div class="form-group">
            <label for="thresholdMinimumDscr">Threshold minimum DSCR</label>
            <input 
              id="thresholdMinimumDscr"
              v-model.number="formData.thresholdMinimumDscr"
              type="number" 
              step="0.01"
              min="0"
              placeholder="0.00"
              :disabled="viewMode"
              @input="updateData"
            />
            <div v-if="errors.thresholdMinimumDscr" class="error-message">{{ errors.thresholdMinimumDscr }}</div>
          </div>

          <div class="form-group">
            <label for="economicIrr">Economic IRR</label>
            <input 
              id="economicIrr"
              v-model.number="formData.economicIrr"
              type="number" 
              step="0.001"
              placeholder="0.000"
              :disabled="viewMode"
              @input="updateData"
            />
            <div class="field-note">Eg. Enter 0.1 when 10%</div>
            <div v-if="errors.economicIrr" class="error-message">{{ errors.economicIrr }}</div>
          </div>

          <div class="form-group">
            <label for="thresholdEconomicIrr">Threshold economic IRR</label>
            <input 
              id="thresholdEconomicIrr"
              v-model.number="formData.thresholdEconomicIrr"
              type="number" 
              step="0.001"
              placeholder="0.000"
              :disabled="viewMode"
              @input="updateData"
            />
            <div class="field-note">Eg. Enter 0.1 when 10%</div>
            <div v-if="errors.thresholdEconomicIrr" class="error-message">{{ errors.thresholdEconomicIrr }}</div>
          </div>
        </div>
      </section>

      <!-- Section H: Foreign Exchange Risk Profile -->
      <section class="form-section">
        <div class="section-header">
          <h3>Section H: Foreign Exchange Risk Profile</h3>
          <span class="section-description">Foreign exchange exposure and risk assessment (Optional)</span>
        </div>
        
        <div class="form-grid">
          <div class="form-group full-width">
            <label class="checkbox-label">
              <span class="checkbox-text">Will the project be exposed to foreign exchange risk?</span>
              <div class="radio-group">
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.foreignExchangeRisk" 
                    value="yes"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  Yes
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.foreignExchangeRisk" 
                    value="no"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  No
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.foreignExchangeRisk" 
                    value="uncertain"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  Uncertain
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.foreignExchangeRisk" 
                    value="skip"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  Skip
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="formData.foreignExchangeRisk" 
                    value="na"
                    :disabled="viewMode"
                    @change="updateData"
                  />
                  N/A
                </label>
              </div>
            </label>
            <div v-if="errors.foreignExchangeRisk" class="error-message">{{ errors.foreignExchangeRisk }}</div>
          </div>

          <div class="form-group">
            <label for="currencyDepreciation">What has been the five-year average national currency depreciation vis-à-vis a benchmark currency?</label>
            <input 
              id="currencyDepreciation"
              v-model.number="formData.currencyDepreciation"
              type="number" 
              step="0.001"
              placeholder="0.000"
              :disabled="viewMode"
              @input="updateData"
            />
            <div class="field-note">Eg. Enter 0.1 when 10%</div>
            <div v-if="errors.currencyDepreciation" class="error-message">{{ errors.currencyDepreciation }}</div>
          </div>
        </div>
      </section>

      <!-- Section I: Climate Change and Resilience Profile -->
      <section class="form-section">
        <div class="section-header">
          <h3>Section I: Climate Change and Resilience Profile</h3>
          <span class="section-description">Climate vulnerability assessment (Optional)</span>
          <div class="section-note">To which of the following climate change events is the proposed project's location vulnerable? Select the top five in descending order of exposure from high to low</div>
        </div>
        
        <div class="form-grid">
          <div class="form-group">
            <label for="climateVulnerability1">1</label>
            <select 
              id="climateVulnerability1"
              v-model="formData.climateVulnerability1"
              :disabled="viewMode"
              @change="updateData"
            >
              <option value="">Select climate event</option>
              <option value="coastal-flood">Coastal Flood</option>
              <option value="cloudbursts">Cloudbursts</option>
              <option value="cyclone-typhoon-hurricane">Cyclone/ Typhoon/ Hurricane</option>
              <option value="droughts">Droughts</option>
              <option value="earthquake">Earthquake</option>
              <option value="extreme-heat">Extreme heat</option>
              <option value="extreme-snowfall-cold">Extreme Snowfall / Cold</option>
              <option value="landslide">Landslide</option>
              <option value="river-flood">River Flood</option>
              <option value="tsunami">Tsunami</option>
              <option value="urban-flood">Urban Flood</option>
              <option value="volcano">Volcano</option>
              <option value="water-scarcity">Water scarcity</option>
              <option value="wildfires">Wildfires</option>
              <option value="others">Others</option>
            </select>
            <div v-if="errors.climateVulnerability1" class="error-message">{{ errors.climateVulnerability1 }}</div>
          </div>

          <div class="form-group">
            <label for="climateVulnerability2">2</label>
            <select 
              id="climateVulnerability2"
              v-model="formData.climateVulnerability2"
              :disabled="viewMode"
              @change="updateData"
            >
              <option value="">Select climate event</option>
              <option value="coastal-flood">Coastal Flood</option>
              <option value="cloudbursts">Cloudbursts</option>
              <option value="cyclone-typhoon-hurricane">Cyclone/ Typhoon/ Hurricane</option>
              <option value="droughts">Droughts</option>
              <option value="earthquake">Earthquake</option>
              <option value="extreme-heat">Extreme heat</option>
              <option value="extreme-snowfall-cold">Extreme Snowfall / Cold</option>
              <option value="landslide">Landslide</option>
              <option value="river-flood">River Flood</option>
              <option value="tsunami">Tsunami</option>
              <option value="urban-flood">Urban Flood</option>
              <option value="volcano">Volcano</option>
              <option value="water-scarcity">Water scarcity</option>
              <option value="wildfires">Wildfires</option>
              <option value="others">Others</option>
            </select>
            <div v-if="errors.climateVulnerability2" class="error-message">{{ errors.climateVulnerability2 }}</div>
          </div>

          <div class="form-group">
            <label for="climateVulnerability3">3</label>
            <select 
              id="climateVulnerability3"
              v-model="formData.climateVulnerability3"
              :disabled="viewMode"
              @change="updateData"
            >
              <option value="">Select climate event</option>
              <option value="coastal-flood">Coastal Flood</option>
              <option value="cloudbursts">Cloudbursts</option>
              <option value="cyclone-typhoon-hurricane">Cyclone/ Typhoon/ Hurricane</option>
              <option value="droughts">Droughts</option>
              <option value="earthquake">Earthquake</option>
              <option value="extreme-heat">Extreme heat</option>
              <option value="extreme-snowfall-cold">Extreme Snowfall / Cold</option>
              <option value="landslide">Landslide</option>
              <option value="river-flood">River Flood</option>
              <option value="tsunami">Tsunami</option>
              <option value="urban-flood">Urban Flood</option>
              <option value="volcano">Volcano</option>
              <option value="water-scarcity">Water scarcity</option>
              <option value="wildfires">Wildfires</option>
              <option value="others">Others</option>
            </select>
            <div v-if="errors.climateVulnerability3" class="error-message">{{ errors.climateVulnerability3 }}</div>
          </div>

          <div class="form-group">
            <label for="climateVulnerability4">4</label>
            <select 
              id="climateVulnerability4"
              v-model="formData.climateVulnerability4"
              :disabled="viewMode"
              @change="updateData"
            >
              <option value="">Select climate event</option>
              <option value="coastal-flood">Coastal Flood</option>
              <option value="cloudbursts">Cloudbursts</option>
              <option value="cyclone-typhoon-hurricane">Cyclone/ Typhoon/ Hurricane</option>
              <option value="droughts">Droughts</option>
              <option value="earthquake">Earthquake</option>
              <option value="extreme-heat">Extreme heat</option>
              <option value="extreme-snowfall-cold">Extreme Snowfall / Cold</option>
              <option value="landslide">Landslide</option>
              <option value="river-flood">River Flood</option>
              <option value="tsunami">Tsunami</option>
              <option value="urban-flood">Urban Flood</option>
              <option value="volcano">Volcano</option>
              <option value="water-scarcity">Water scarcity</option>
              <option value="wildfires">Wildfires</option>
              <option value="others">Others</option>
            </select>
            <div v-if="errors.climateVulnerability4" class="error-message">{{ errors.climateVulnerability4 }}</div>
          </div>

          <div class="form-group">
            <label for="climateVulnerability5">5</label>
            <select 
              id="climateVulnerability5"
              v-model="formData.climateVulnerability5"
              :disabled="viewMode"
              @change="updateData"
            >
              <option value="">Select climate event</option>
              <option value="coastal-flood">Coastal Flood</option>
              <option value="cloudbursts">Cloudbursts</option>
              <option value="cyclone-typhoon-hurricane">Cyclone/ Typhoon/ Hurricane</option>
              <option value="droughts">Droughts</option>
              <option value="earthquake">Earthquake</option>
              <option value="extreme-heat">Extreme heat</option>
              <option value="extreme-snowfall-cold">Extreme Snowfall / Cold</option>
              <option value="landslide">Landslide</option>
              <option value="river-flood">River Flood</option>
              <option value="tsunami">Tsunami</option>
              <option value="urban-flood">Urban Flood</option>
              <option value="volcano">Volcano</option>
              <option value="water-scarcity">Water scarcity</option>
              <option value="wildfires">Wildfires</option>
              <option value="others">Others</option>
            </select>
            <div v-if="errors.climateVulnerability5" class="error-message">{{ errors.climateVulnerability5 }}</div>
          </div>
        </div>
      </section>

      <!-- Section J: GHG Emissions Profile -->
      <section class="form-section">
        <div class="section-header">
          <h3>Section J: GHG Emissions Profile</h3>
          <span class="section-description">Greenhouse gas emissions assessment (Optional)</span>
        </div>
        
        <div class="form-grid">
          <div class="form-group">
            <label for="annualGhgEmissions">Estimated average annual GHG emissions from the proposed project operations</label>
            <div class="input-with-suffix">
              <input 
                id="annualGhgEmissions"
                v-model.number="formData.annualGhgEmissions"
                type="number" 
                step="0.1"
                min="0"
                placeholder="0.0"
                :disabled="viewMode"
                @input="updateData"
              />
              <span class="input-suffix">CO2e tonnes</span>
            </div>
            <div v-if="errors.annualGhgEmissions" class="error-message">{{ errors.annualGhgEmissions }}</div>
          </div>

          <div class="form-group">
            <label for="ghgReductionPotential">Potential annual average reduction in GHG emissions through carbon offset measures</label>
            <div class="input-with-suffix">
              <input 
                id="ghgReductionPotential"
                v-model.number="formData.ghgReductionPotential"
                type="number" 
                step="0.1"
                min="0"
                placeholder="0.0"
                :disabled="viewMode"
                @input="updateData"
              />
              <span class="input-suffix">CO2e tonnes</span>
            </div>
            <div v-if="errors.ghgReductionPotential" class="error-message">{{ errors.ghgReductionPotential }}</div>
          </div>
        </div>
      </section>

      <!-- Additional Information Section -->
      <section class="form-section">
        <div class="section-header">
          <h3>Additional Information</h3>
          <span class="section-description">Optional details for enhanced assessment</span>
        </div>
        
        <div class="form-grid">
          <div class="form-group">
            <label for="expectedDuration">Expected Project Duration (years)</label>
            <input 
              id="expectedDuration"
              v-model.number="formData.expectedDuration"
              type="number" 
              step="0.5"
              min="0"
              placeholder="e.g., 5.5"
              :disabled="viewMode"
              @input="updateData"
            />
          </div>

          <div class="form-group">
            <label for="expectedStartDate">Expected Start Date</label>
            <input 
              id="expectedStartDate"
              v-model="formData.expectedStartDate"
              type="date"
              :disabled="viewMode"
              @input="updateData"
            />
          </div>

          <div class="form-group full-width">
            <label for="keyStakeholders">Key Stakeholders</label>
            <textarea 
              id="keyStakeholders"
              v-model="formData.keyStakeholders"
              rows="3"
              placeholder="List key stakeholders, partners, and beneficiaries"
              :disabled="viewMode"
              @input="updateData"
            ></textarea>
          </div>

          <div class="form-group full-width">
            <label for="strategicObjectives">Strategic Objectives Alignment</label>
            <textarea 
              id="strategicObjectives"
              v-model="formData.strategicObjectives"
              rows="3"
              placeholder="Describe how this project aligns with national/regional strategic objectives"
              :disabled="viewMode"
              @input="updateData"
            ></textarea>
          </div>
        </div>
      </section>

      <!-- Validation Summary -->
      <div v-if="showValidation" class="validation-summary">
        <div v-if="isValid" class="validation-success">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M9 12l2 2 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
          </svg>
          All required fields are completed. You can proceed to the next tab.
        </div>
        <div v-else class="validation-error">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
            <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" stroke-width="2"/>
            <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" stroke-width="2"/>
          </svg>
          Please complete all required fields before proceeding.
        </div>
      </div>

    </form>
  </div>
</template>

<script>
export default {
  name: 'BasicProjectDataTab',
  props: {
    projectData: {
      type: Object,
      default: () => ({})
    },
    viewMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      formData: {
        // Basic Information
        projectName: '',
        implementingAgency: '',
        stage: '',
        submissionDate: '',
        privatelyInitiated: '',
        
        // Section A: Project Profile
        location: '',
        sector: '',
        projectType: '',
        currency: '',
        projectSize: null,
        projectDescription: '',
        
        // Section B: PREREQUISITE
        nationalPlanDerived: '',
        economicallySound: '',
        privateSectorParticipation: '',
        
        // Section C: Revenue Profile
        revenueSource: '',
        
        // Section D: Government Support
        governmentSupportRequired: '',
        directCommitments: null,
        contingentLiabilities: null,
        
        // Section E: Land Status
        landUnit: '',
        landAcquisitionRequired: '',
        totalLandRequired: null,
        landAvailable: null,
        potentialLandOwners: '',
        landAcquisitionResponsibility: '',
        
        // Section F: Value for Money - Quantitative Assessment
        valueForMoney: null,
        thresholdValueForMoney: null,
        
        // Section G: Return Expectations
        projectIrrBase: null,
        projectIrrStress: null,
        thresholdProjectIrr: null,
        minimumDscrBase: null,
        minimumDscrStress: null,
        thresholdMinimumDscr: null,
        economicIrr: null,
        thresholdEconomicIrr: null,
        
        // Section H: Foreign Exchange Risk Profile
        foreignExchangeRisk: '',
        currencyDepreciation: null,
        
        // Section I: Climate Change and Resilience Profile
        climateVulnerability1: '',
        climateVulnerability2: '',
        climateVulnerability3: '',
        climateVulnerability4: '',
        climateVulnerability5: '',
        
        // Section J: GHG Emissions Profile
        annualGhgEmissions: null,
        ghgReductionPotential: null,
        
        // Additional Information
        expectedDuration: null,
        expectedStartDate: '',
        keyStakeholders: '',
        strategicObjectives: ''
      },
      errors: {},
      showValidation: false
    }
  },
  computed: {
    isValid() {
      return this.validateForm().isValid
    },
    characterCount() {
      return this.formData.projectDescription.length
    }
  },
  watch: {
    projectData: {
      handler(newData) {
        if (newData && Object.keys(newData).length > 0) {
          this.formData = { ...this.formData, ...newData }
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    updateData() {
      this.$emit('update', this.formData)
      this.validateForm()
    },
    validateForm() {
      this.errors = {}
      let isValid = true

      // Required field validation (Section A only)
      const requiredFields = [
        'projectName',
        'implementingAgency', 
        'stage',
        'submissionDate',
        'privatelyInitiated'
      ]

      requiredFields.forEach(field => {
        if (!this.formData[field] || this.formData[field] === '') {
          this.errors[field] = 'This field is required'
          isValid = false
        }
      })

      // Project name validation
      if (this.formData.projectName && this.formData.projectName.length < 3) {
        this.errors.projectName = 'Project name must be at least 3 characters'
        isValid = false
      }

      // Project size validation (optional field)
      if (this.formData.projectSize !== null && this.formData.projectSize !== undefined && this.formData.projectSize <= 0) {
        this.errors.projectSize = 'Project size must be greater than 0'
        isValid = false
      }

      // Percentage validation for government support fields
      if (this.formData.directCommitments !== null && this.formData.directCommitments !== undefined) {
        if (this.formData.directCommitments < 0 || this.formData.directCommitments > 100) {
          this.errors.directCommitments = 'Direct commitments must be between 0 and 100'
          isValid = false
        }
      }

      if (this.formData.contingentLiabilities !== null && this.formData.contingentLiabilities !== undefined) {
        if (this.formData.contingentLiabilities < 0 || this.formData.contingentLiabilities > 100) {
          this.errors.contingentLiabilities = 'Contingent liabilities must be between 0 and 100'
          isValid = false
        }
      }

      // Land validation (when land acquisition is required)
      if (this.formData.landAcquisitionRequired === 'yes') {
        if (this.formData.totalLandRequired !== null && this.formData.totalLandRequired !== undefined && this.formData.totalLandRequired <= 0) {
          this.errors.totalLandRequired = 'Total land required must be greater than 0'
          isValid = false
        }

        if (this.formData.landAvailable !== null && this.formData.landAvailable !== undefined && this.formData.landAvailable < 0) {
          this.errors.landAvailable = 'Land available cannot be negative'
          isValid = false
        }

        // Check if land available exceeds total land required
        if (this.formData.totalLandRequired && this.formData.landAvailable && 
            this.formData.landAvailable > this.formData.totalLandRequired) {
          this.errors.landAvailable = 'Land available cannot exceed total land required'
          isValid = false
        }
      }

      // Validation for numeric fields (ensure they are not negative where applicable)
      const numericFields = [
        'valueForMoney', 'thresholdValueForMoney', 'minimumDscrBase', 
        'minimumDscrStress', 'thresholdMinimumDscr', 'annualGhgEmissions', 
        'ghgReductionPotential'
      ]
      
      numericFields.forEach(field => {
        if (this.formData[field] !== null && this.formData[field] !== undefined && this.formData[field] < 0) {
          this.errors[field] = 'Value cannot be negative'
          isValid = false
        }
      })

      // Validation for climate vulnerability - ensure no duplicates
      const climateVulnerabilities = [
        this.formData.climateVulnerability1,
        this.formData.climateVulnerability2,
        this.formData.climateVulnerability3,
        this.formData.climateVulnerability4,
        this.formData.climateVulnerability5
      ].filter(v => v && v !== '')

      const uniqueVulnerabilities = new Set(climateVulnerabilities)
      if (climateVulnerabilities.length !== uniqueVulnerabilities.size) {
        ['climateVulnerability1', 'climateVulnerability2', 'climateVulnerability3', 'climateVulnerability4', 'climateVulnerability5'].forEach(field => {
          this.errors[field] = 'Please select unique climate events'
        })
        isValid = false
      }

      // Character limit validation
      if (this.formData.projectDescription.length > 1000) {
        this.errors.projectDescription = 'Description must be 1000 characters or less'
        isValid = false
      }

      // Date validation
      if (this.formData.submissionDate) {
        const submissionDate = new Date(this.formData.submissionDate)
        const today = new Date()
        if (submissionDate > today) {
          this.errors.submissionDate = 'Submission date cannot be in the future'
          isValid = false
        }
      }

      if (this.formData.expectedStartDate && this.formData.submissionDate) {
        const startDate = new Date(this.formData.expectedStartDate)
        const submissionDate = new Date(this.formData.submissionDate)
        if (startDate <= submissionDate) {
          this.errors.expectedStartDate = 'Expected start date should be after submission date'
          isValid = false
        }
      }

      this.showValidation = true
      this.$emit('validate', isValid)
      
      return { isValid, errors: this.errors }
    },
    validateAndSubmit() {
      this.validateForm()
    },
    getCurrencyDisplay() {
      const currencyMap = {
        'usd-million': 'USD Million',
        'euro-million': 'Euro Million',
        'local-currency-million': 'Local Currency Million'
      }
      return currencyMap[this.formData.currency] || 'Million'
    },
    getLandUnitDisplay() {
      const landUnitMap = {
        'hectares': 'Hectares',
        'acres': 'Acres'
      }
      return landUnitMap[this.formData.landUnit] || 'Units'
    },
    showLearnMore() {
      alert('Learn More: This section contains prerequisite questions that help assess the project\'s alignment with national priorities and economic viability. These questions are essential for determining the project\'s suitability for further analysis.')
    }
  },
  mounted() {
    // Initial validation
    this.$nextTick(() => {
      this.validateForm()
    })
  },
  
}
</script>

<style scoped>
.basic-project-data-tab {
  max-width: 1000px;
  margin: 0 auto;
}

.tab-header {
  text-align: center;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 2px solid #e9ecef;
}

.tab-header h2 {
  font-size: 2rem;
  color: #333;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.tab-description {
  color: #666;
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
}

.form-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  border-left: 4px solid #00338d;
}

.section-header {
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.section-header h3 {
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.required-indicator {
  color: #dc3545;
  font-size: 0.9rem;
  font-weight: 500;
}

.section-description {
  color: #666;
  font-size: 0.95rem;
  font-style: italic;
}

.section-note {
  color: #555;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: #f0f4f8;
  border-radius: 6px;
  border-left: 3px solid #00338d;
}

.field-note {
  color: #666;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  font-style: italic;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
  display: block;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.75rem;
  border: 2px solid #e0e6ed;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s, box-shadow 0.3s;
  background: white;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #00338d;
  box-shadow: 0 0 0 3px rgba(0, 51, 141, 0.1);
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
}

.input-with-suffix {
  position: relative;
  display: flex;
  align-items: center;
}

.input-with-suffix input {
  flex: 1;
  padding-right: 120px;
}

.input-suffix {
  position: absolute;
  right: 12px;
  color: #666;
  font-size: 0.9rem;
  font-weight: 500;
  background: white;
  padding: 0 0.5rem;
}

.checkbox-label {
  display: block !important;
}

.checkbox-text {
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
  display: block;
}

.radio-group {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
  align-items: stretch;
}

.radio-group.yes-no {
  grid-template-columns: repeat(2, minmax(80px, 120px));
  justify-content: start;
}

.radio-option {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: 500;
  padding: 0.75rem 1rem;
  min-height: 40px;
  position: relative;
  transition: all 0.2s ease;
  border-radius: 8px;
  border: 2px solid #d1d5db;
  background: #ffffff;
  color: #374151;
  text-align: center;
}

.radio-option input[type="radio"] {
  display: none;
}

/* Radio custom elements removed - no longer needed */

.radio-option:has(input[type="radio"]:checked) {
  background: #00338d;
  border-color: #00338d;
  color: white;
  font-weight: 600;
  box-shadow: 0 0 0 2px rgba(0, 51, 141, 0.2);
}

/* Fallback for browsers that don't support :has() */
.radio-option.selected {
  background: #00338d;
  border-color: #00338d;
  color: white;
  font-weight: 600;
  box-shadow: 0 0 0 2px rgba(0, 51, 141, 0.2);
}

.radio-option.selected:hover {
  background: #002966;
  border-color: #002966;
  color: white;
}

.radio-option:hover {
  background-color: #e8f0fe;
  border-color: #00338d;
  color: #00338d;
}

.radio-option:hover:has(input[type="radio"]:checked) {
  background: #002966;
  border-color: #002966;
  color: white;
}

/* Button-style radio buttons - no animation needed */

.radio-option:has(input[type="radio"]:focus) {
  border-color: #00338d;
  box-shadow: 0 0 0 3px rgba(0, 51, 141, 0.1);
  outline: none;
}

.radio-option:has(input[type="radio"]:focus-visible) {
  outline: 2px solid #00338d;
  outline-offset: 2px;
}

.character-count {
  font-size: 0.8rem;
  color: #666;
  text-align: right;
  margin-top: 0.25rem;
}

.error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  font-weight: 500;
}

.validation-summary {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 2rem;
  border: 2px solid #e9ecef;
}

.validation-success {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #28a745;
  font-weight: 600;
}

.validation-error {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #dc3545;
  font-weight: 600;
}

.validation-success svg,
.validation-error svg {
  flex-shrink: 0;
}

/* Learn more button removed */

/* Responsive Design */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .radio-group {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    margin-top: 0.5rem;
  }
  
  .radio-option {
    padding: 0.75rem 0.75rem;
    min-height: 44px;
    font-size: 0.95rem;
  }
  
  /* Section title styling removed */
  
  .form-section {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .radio-group {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .radio-option {
    padding: 1rem 0.75rem;
    min-height: 48px;
    font-size: 0.9rem;
  }
}
</style>